import { Subject } from 'rxjs';
import { UUID } from '../models/classes/utilities';

export enum eWorkerTasks {
    usdConvertGeometry = 'usdConvertGeometry',
    usdPackage = 'usdPackageStart',
}

export enum eKB3DMessageType {
    taskStart = 'taskStart',
    taskError = 'taskError',
    taskProgress = 'taskProgress',
    taskComplete = 'taskComplete',
}

export interface IKB3DMessage<T = any> {
    type: eKB3DMessageType;
    payload: T;
}

export interface IKB3DTaskMessage<T = any> extends IKB3DMessage<T> {
    task: eWorkerTasks;
    taskId: string;
}

const THREAD_COUNT = 2;

export class Kb3dWorker {
    workerPool: Worker[] = [];
    toggle = 0;
    requestsInFlight = new Map<string, Subject<IKB3DTaskMessage>>();

    public static workerPath = '';

    constructor() {
        if (!Kb3dWorker.workerPath) {
            throw Error('Worker Script path not set');
        }
        const workerPath = Kb3dWorker.workerPath;

        const content = `importScripts( "${workerPath}" );`;
        const workerBlobUrl = URL.createObjectURL(new Blob([content], { type: 'text/javascript' }));

        const workerReturnMessage = (event: MessageEvent<IKB3DMessage | IKB3DTaskMessage>) => {
            if ('task' in event.data) {
                const data = event.data;
                const sub = this.requestsInFlight.get(data.taskId);

                if (sub) {
                    // We have to remove the request from the inflight map first because the sub.next can trigger the dispose
                    // But the sub.complete has to be after the sub.next
                    if (data.type === eKB3DMessageType.taskComplete) {
                        this.requestsInFlight.delete(data.taskId);
                    }

                    if (data.type === eKB3DMessageType.taskError) {
                        sub.error(data.payload);
                    } else {
                        sub.next(data);
                    }

                    if (data.type === eKB3DMessageType.taskComplete) {
                        sub.complete();
                    }
                }
            }
        };

        for (let i = 0; i < THREAD_COUNT; i++) {
            const newWorker = new Worker(workerBlobUrl);
            newWorker.onmessage = workerReturnMessage;
            this.workerPool.push(newWorker);
        }
        URL.revokeObjectURL(workerBlobUrl);
    }

    startTask<R = any, S = any>(task: eWorkerTasks, taskPayload: S, transfer?: ArrayBuffer[]) {
        const messagePayload: IKB3DTaskMessage<S> = {
            type: eKB3DMessageType.taskStart,
            task,
            taskId: UUID.generate(),
            payload: taskPayload,
        };
        this.workerPool[this.toggle].postMessage(messagePayload, transfer ? { transfer } : undefined);

        const sub = new Subject<IKB3DTaskMessage<R>>();
        this.requestsInFlight.set(messagePayload.taskId, sub);
        this.toggle = (this.toggle + 1) % THREAD_COUNT;

        return sub;
    }

    dispose() {
        this.workerPool.forEach(worker => {
            worker.terminate();
        });

        this.requestsInFlight.forEach(sub => {
            sub.error('Worker Terminated');
            sub.complete();
        });
        this.requestsInFlight.clear();
    }
}
